import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';

import Navbar from './components/Navbar';
import Footer from './components/Footer';
import { useHardwareOptimization } from './hooks/useHardwareOptimization';
import { useRealTime } from './hooks/useRealTime';
import { useQuery, gql } from '@apollo/client';

const GET_PUBLIC_STATS = gql`
  query GetPublicStats {
    publicStats {
      totalRegistrations
      cashfreeRegistrations
      razorpayRegistrations
      totalVisitors
      totalClicks
    }
  }
`;

// Pages - Public (Eager load Home for better LCP/FCP)
import Home from './pages/Home';
const About = lazy(() => import('./pages/About'));
const Olympiads = lazy(() => import('./pages/Olympiads'));
const Gallery = lazy(() => import('./pages/Gallery'));
const Results = lazy(() => import('./pages/Results'));
const FAQ = lazy(() => import('./pages/FAQ'));
const Contact = lazy(() => import('./pages/Contact'));
const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsConditions = lazy(() => import('./pages/TermsConditions'));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy'));

// Pages - Admin (Phase 1-5)
const AdminLayout = lazy(() => import('./admin/AdminLayout'));
const AdminDatabase = lazy(() => import('./admin/AdminDatabase'));
const AdminCMS = lazy(() => import('./admin/AdminCMS'));
const AdminCRM = lazy(() => import('./admin/AdminCRM'));
const AdminDevOps = lazy(() => import('./admin/AdminDevOps'));
const AdminMarketing = lazy(() => import('./admin/AdminMarketing'));
const AdminSecurity = lazy(() => import('./admin/AdminSecurity'));

const NotFound = () => (
  <div style={{ textAlign: 'center', padding: '5rem 2rem', minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
    <Helmet>
      <title>404 - Page Not Found | Dynamic Exams</title>
      <meta name="robots" content="noindex, nofollow" />
    </Helmet>
    <h1 style={{ fontSize: '4rem', color: 'var(--primary)', marginBottom: '1rem' }}>404</h1>
    <h2 style={{ fontSize: '2rem', marginBottom: '2rem' }}>Page Not Found</h2>
    <p style={{ marginBottom: '2rem' }}>The page you are looking for does not exist or has been moved.</p>
    <Link to="/" className="btn btn-primary">Return Home</Link>
  </div>
);

function CanonicalMeta() {
  const location = useLocation();
  const path = location.pathname === '/' ? '/' : location.pathname.replace(/\/$/, '');
  const canonicalUrl = `https://dynamicexams.org${path}`;
  
  return (
    <Helmet>
      <link rel="canonical" href={canonicalUrl} />
      {/* Hreflang Tags for Internationalization SEO Sync */}
      <link rel="alternate" hrefLang="en" href={canonicalUrl} />
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />
      {/* Global Fallback OpenGraph and Twitter Tags */}
      <meta property="og:site_name" content="Dynamic Exams" />
      <meta property="og:image" content="https://dynamicexams.org/olympiads-banner.webp" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Dynamic Exams | Olympiads" />
      <meta name="twitter:image" content="https://dynamicexams.org/olympiads-banner.webp" />
    </Helmet>
  );
}

function PublicLayout({ children }) {
  // Setup UTM tracking on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const source = urlParams.get('utm_source');
    const medium = urlParams.get('utm_medium');
    const campaign = urlParams.get('utm_campaign');
    
    if (source || medium || campaign) {
      sessionStorage.setItem('utmParams', JSON.stringify({ source, medium, campaign }));
    }

    if (!sessionStorage.getItem('visitor_tracked')) {
      // 🚀 GOD MODE: Zero-Allocation Tracking + Zero-Loss Transport
      // Bypasses JSON parsing on backend (GET) while guaranteeing OS delivery on tab close (keepalive).
      fetch(`/api/analytics/track?event=visitor&t=${Date.now()}`, { method: 'GET', keepalive: true }).catch(() => {});
      sessionStorage.setItem('visitor_tracked', 'true');
    }

    // Defer theme fetching to avoid blocking React Hydration (INP optimization)
    const timer = setTimeout(() => {
      fetch('/api/theme')
        .then(res => res.json())
        .then(theme => {
          if (theme.primary_color) {
            document.documentElement.style.setProperty('--primary', theme.primary_color);
          }
          if (theme.bg_color) {
            document.documentElement.style.setProperty('--bg-base', theme.bg_color);
          }
        })
        .catch(console.error);
    }, 50);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="page-wrapper">
      <CanonicalMeta />
      <div className="circuit-glow"></div>
      <Navbar />
      <main className="main-content">
        <Suspense fallback={<div className="loading-spinner">Loading...</div>}>
          {children}
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}

function ProtectedAdminRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);

  useEffect(() => {
    fetch('/api/admin/verify')
      .then(res => {
        if (res.ok) setIsAuthenticated(true);
        else setIsAuthenticated(false);
      })
      .catch(() => setIsAuthenticated(false));
  }, []);

  if (isAuthenticated === null) {
    return <div className="h-screen w-screen flex items-center justify-center bg-gray-900 text-[#00e5ff] font-mono tracking-widest uppercase">Verifying Security Token...</div>;
  }

  return isAuthenticated ? children : <Navigate to="/admin-portal" replace />;
}

function App() {
  // Defer hardware and real-time init (INP optimization)
  const [initBackgroundTasks, setInitBackgroundTasks] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setInitBackgroundTasks(true);
    }, 200);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <AppRouter />
      {initBackgroundTasks && <BackgroundTasks />}
    </>
  );
}

function BackgroundTasks() {
  useHardwareOptimization(); 
  useRealTime(); 
  
  // Fetch GraphQL data silently without caching to prevent Apollo Warning 17
  const { data } = useQuery(GET_PUBLIC_STATS, { fetchPolicy: 'no-cache' });
  useEffect(() => {
    if (data) {
      console.log(`📊 [GraphQL] Traffic Stats: ${data.publicStats.totalVisitors} visitors | ${data.publicStats.totalClicks} clicks`);
      console.log(`💰 [GraphQL] Gateway Stats: ${data.publicStats.totalRegistrations} total (${data.publicStats.cashfreeRegistrations} Cashfree, ${data.publicStats.razorpayRegistrations} Razorpay)`);
    }
  }, [data]);

  return null;
}

function AppRouter() {
  return (
    <Router>
      <Routes>
        {/* PUBLIC ROUTES */}
        <Route path="/*" element={
          <PublicLayout>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/olympiads" element={<Olympiads />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/results" element={<Results />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-and-conditions" element={<TermsConditions />} />
              <Route path="/refund-and-cancellation" element={<RefundPolicy />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </PublicLayout>
        } />

        {/* ADMIN LOGIN */}
        <Route path="/admin-portal" element={
          <Suspense fallback={<div className="h-screen w-screen flex items-center justify-center bg-gray-950 text-blue-500 font-mono text-xl font-bold">ADMIN PORTAL INITIALIZING...</div>}>
            <AdminLogin />
          </Suspense>
        } />

        {/* ADMIN ROUTES */}
        <Route path="/admin/*" element={
          <ProtectedAdminRoute>
            <Suspense fallback={<div className="h-screen w-screen flex items-center justify-center bg-gray-900 text-[#00e5ff] font-mono">Initializing Biometric Security...</div>}>
              <AdminLayout>
                <Suspense fallback={<div className="h-full w-full flex items-center justify-center text-blue-500 font-mono animate-pulse">Loading Panel...</div>}>
                  <Routes>
                    <Route path="/" element={<Navigate to="/admin/database" replace />} />
                    <Route path="/database" element={<AdminDatabase />} />
                    <Route path="/cms" element={<AdminCMS />} />
                    <Route path="/crm" element={<AdminCRM />} />
                    <Route path="/devops" element={<AdminDevOps />} />
                    <Route path="/marketing" element={<AdminMarketing />} />
                    <Route path="/security" element={<AdminSecurity />} />
                  </Routes>
                </Suspense>
              </AdminLayout>
            </Suspense>
          </ProtectedAdminRoute>
        } />
      </Routes>
    </Router>
  );
}

export default App;
